/**
 * DataGrid Component
 * Reusable data grid with pagination, search, and REST API integration
 */

import { IComponent } from './IComponent';
import { Logger } from '../services/Logger';
import { ModalSystem } from '../services/ModalSystem';
import { ToasterService } from '../services/ToasterService';
import { userSettingsService } from '../services/UserSettingsService';
import { AppSettings } from '../constants/Settings';
import { DatePicker } from './DatePicker';

const logger = Logger.scope('DataGrid');

export interface DataGridColumn {
  field: string;
  header: string;
  width?: string;
  sortable?: boolean;
  filterable?: boolean; // Enable column-level filtering
  filterType?: 'text' | 'date'; // Type of filter UI to show (default: 'text')
  formatter?: (value: any, row: any) => string;
  align?: 'left' | 'center' | 'right';
  visible?: boolean;
  cellClass?: string; // Additional CSS classes for cells
}

// Filter value can be simple string or complex with operator
export interface ColumnFilterValue {
  value: string;
  operator?: 'eq' | 'lt' | 'gt' | 'like'; // equals, less than, greater than, like
}

export interface DataGridOptions {
  columns: DataGridColumn[];
  pageSize?: number;
  searchable?: boolean;
  sortable?: boolean;
  selectable?: boolean;
  mode?: 'client' | 'server'; // 'client' = load all data, filter locally; 'server' = server-side pagination/filter/sort
  apiEndpoint?: string; // REST API endpoint for server mode
  localData?: any[]; // Data for client mode
  idField?: string | ((row: any) => string); // Custom field or function to get unique row ID
  onRowClick?: (row: any) => void;
  onRowDoubleClick?: (row: any) => void; // Double-click for drill-down navigation
  onRowSelect?: (rows: any[]) => void;
  onDataLoaded?: (data: any[], total: number) => void; // Called when data is loaded (client or server mode)
  
  // Server-side callbacks - when set, DataGrid delegates to external handler instead of internal logic
  onSort?: (column: string, direction: 'asc' | 'desc') => void;
  onSearch?: (searchText: string) => void;
  onPageChange?: (page: number, pageSize: number) => void;
  onColumnFilter?: (filters: Map<string, string | ColumnFilterValue>) => void; // Column-level filtering

  // Initial state (useful when re-creating grid with preserved state)
  initialSearchTerm?: string;
  initialSortColumn?: string;
  initialSortDirection?: 'asc' | 'desc';
  initialPage?: number;
  autoFocusSearch?: boolean;
  
  // Total records (required for server mode when providing localData)
  totalRecords?: number;

  // Callback for column changes (reorder, resize, visibility)
  onColumnChange?: (columns: DataGridColumn[]) => void;

  // Initial grid properties
  pagingPosition?: 'top' | 'bottom' | 'both';
  horizontalScroll?: boolean;
  
  // Custom fetch headers for API calls (useful for authentication)
  fetchHeaders?: Record<string, string> | (() => Record<string, string>);
  
  // Toolbar visibility - when false, hide the search bar and toolbar buttons (refresh, columns, properties, export)
  // Use this when you want to provide your own toolbar with custom buttons
  showToolbar?: boolean;
}

export interface PaginationInfo {
  currentPage: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
}

export class DataGrid implements IComponent {
  private container: HTMLElement;
  private options: DataGridOptions;
  private data: any[] = [];
  private filteredData: any[] = [];
  private currentPage: number = 1;
  private pageSize: number = 5;
  private searchTerm: string = '';
  private sortColumn: string = '';
  private sortDirection: 'asc' | 'desc' | 'none' = 'none';
  private selectedRows: Set<any> = new Set();
  private mode: 'client' | 'server' = 'client';
  private serverTotalRecords: number = 0; // Total records on server (for server mode pagination)
  private searchDebounceTimer: number | null = null;
  private isInitializing: boolean = true; // Track if grid is still initializing (show skeleton)
  private columnFilters: Map<string, string | ColumnFilterValue> = new Map(); // Column-level filters

  // Column resizing state
  private resizingColumn: DataGridColumn | null = null;
  private resizeStartX: number = 0;
  private resizeStartWidth: number = 0;
  private resizeHandler: ((e: MouseEvent) => void) | null = null;
  private stopResizeHandler: (() => void) | null = null;

  // DOM elements
  private headerRow!: HTMLTableRowElement;
  private tbody!: HTMLTableSectionElement;
  private searchInput!: HTMLInputElement;
  private loadingEl!: HTMLElement;
  private emptyEl!: HTMLElement;
  private tableWrapper!: HTMLElement;
  private paginationElements!: {
    rangeStart: HTMLElement[];
    rangeEnd: HTMLElement[];
    total: HTMLElement[];
    currentPage: HTMLElement[];
    totalPages: HTMLElement[];
    firstBtn: HTMLButtonElement[];
    prevBtn: HTMLButtonElement[];
    nextBtn: HTMLButtonElement[];
    lastBtn: HTMLButtonElement[];
    pageSizeSelect: HTMLSelectElement[];
  };

  constructor(container: HTMLElement, options: DataGridOptions) {
    this.container = container;
    this.options = options;
    
    // Use page size priority: explicit option > user settings > hardcoded default
    const userDefaultPageSize = userSettingsService.get<number>(
      AppSettings.DEFAULT_PAGE_SIZE.key, 
      AppSettings.DEFAULT_PAGE_SIZE.defaultValue
    );
    this.pageSize = options.pageSize ?? userDefaultPageSize ?? 25;
    
    // Determine mode: server if apiEndpoint provided, otherwise client
    if (options.mode) {
      this.mode = options.mode;
    } else {
      this.mode = options.apiEndpoint ? 'server' : 'client';
    }

    // Initialize state from options
    if (options.initialSearchTerm) this.searchTerm = options.initialSearchTerm;
    if (options.initialSortColumn) this.sortColumn = options.initialSortColumn;
    if (options.initialSortDirection) this.sortDirection = options.initialSortDirection || 'none';
    if (options.initialPage) this.currentPage = options.initialPage;
    if (options.totalRecords !== undefined) this.serverTotalRecords = options.totalRecords;
  }

  /**
   * Get a value from a row using a field path that may include dots for nested properties
   * e.g., 'product.name' will access row.product.name
   */
  private getNestedValue(row: any, field: string): any {
    if (!field.includes('.')) {
      return row[field];
    }
    
    // Handle nested properties like 'product.name'
    const parts = field.split('.');
    let value = row;
    for (const part of parts) {
      if (value == null) return undefined;
      value = value[part];
    }
    return value;
  }

  /**
   * Initialize the data grid
   */
  async initialize(): Promise<void> {
    // Load template if container is empty (self-contained component)
    await this.loadTemplate();
    
    // Initialize pagination structure (clone bottom to top)
    this.initializePaginationStructure();
    
    this.bindDOMElements();
    
    // Show skeleton loading immediately while we load data
    this.showLoading();
    
    this.setupEventListeners();

    // Apply initial grid properties if provided
    if (this.options.pagingPosition || this.options.horizontalScroll !== undefined) {
      this.setGridProperties({
        pagingPosition: this.options.pagingPosition,
        horizontalScroll: this.options.horizontalScroll
      });
    }
    
    // Load initial data based on mode
    if (this.mode === 'client') {
      // Client mode: Load all data upfront
      if (this.options.localData) {
        this.data = this.options.localData;
        this.filteredData = [...this.data];
      } else if (this.options.apiEndpoint) {
        // Client mode can also load from API (fetches all records once)
        await this.loadAllDataFromAPI();
      }
    } else {
      // Server mode: Load first page from API
      if (this.options.localData) {
        // If local data provided in server mode, use it as the current page
        this.data = this.options.localData;
        this.filteredData = [...this.data];
        // serverTotalRecords should have been set via options.totalRecords
      } else {
        await this.loadPageFromAPI();
      }
    }

    this.renderHeaders();
    this.render();
    
    // Hide skeleton after initial data is rendered
    this.hideLoading();
    this.isInitializing = false;
  }

  /**
   * Load the DataGrid HTML template into the container
   * Only loads if container doesn't already have the template
   */
  private async loadTemplate(): Promise<void> {
    // Check if template is already loaded
    const existingHeader = this.container.querySelector('[data-datagrid-header]');
    if (existingHeader) {
      logger.debug('DataGrid template already loaded, skipping fetch');
      return; // Template already exists
    }
    
    logger.debug('DataGrid loading template from /components/DataGrid.html');
    // Load template from component HTML file
    const response = await fetch('/components/DataGrid.html');
    if (!response.ok) {
      throw new Error(`Failed to load DataGrid template: ${response.status} ${response.statusText}`);
    }
    const template = await response.text();
    this.container.innerHTML = template;
    logger.debug('DataGrid template loaded successfully');
  }

  /**
   * Initialize pagination structure (clone bottom to top)
   */
  private initializePaginationStructure(): void {
    const topPagination = this.container.querySelector('[data-datagrid-pagination-top]');
    const bottomPagination = this.container.querySelector('[data-datagrid-pagination-bottom]');
    
    // Check if top pagination exists and is effectively empty (ignoring comments/whitespace)
    if (topPagination && bottomPagination && topPagination.children.length === 0) {
      // Clone content from bottom to top
      topPagination.innerHTML = bottomPagination.innerHTML;
    }
  }

  /**
   * Bind DOM elements
   */
  private bindDOMElements(): void {
    this.headerRow = this.container.querySelector('[data-datagrid-header]')!;
    this.tbody = this.container.querySelector('[data-datagrid-body]')!;
    this.searchInput = this.container.querySelector('[data-datagrid-search]')!;
    this.loadingEl = this.container.querySelector('[data-datagrid-loading]')!;
    this.emptyEl = this.container.querySelector('[data-datagrid-empty]')!;
    this.tableWrapper = this.container.querySelector('[data-datagrid-table-wrapper]')!;

    // Hide entire toolbar if showToolbar is false
    if (this.options.showToolbar === false) {
      const toolbar = this.container.querySelector('.flex.justify-between.items-center.mb-4');
      if (toolbar) {
        (toolbar as HTMLElement).style.display = 'none';
      }
    }
    // Hide search box if searchable is false (only applies when toolbar is visible)
    else if (this.options.searchable === false && this.searchInput) {
      const searchContainer = this.searchInput.closest('.form-control');
      if (searchContainer) {
        (searchContainer as HTMLElement).style.display = 'none';
      }
    }

    // Helper to get all elements
    const getAll = <T extends Element>(selector: string): T[] => 
      Array.from(this.container.querySelectorAll(selector)) as T[];

    this.paginationElements = {
      rangeStart: getAll('[data-datagrid-range-start]'),
      rangeEnd: getAll('[data-datagrid-range-end]'),
      total: getAll('[data-datagrid-total]'),
      currentPage: getAll('[data-datagrid-current-page]'),
      totalPages: getAll('[data-datagrid-total-pages]'),
      firstBtn: getAll('[data-datagrid-first]'),
      prevBtn: getAll('[data-datagrid-prev]'),
      nextBtn: getAll('[data-datagrid-next]'),
      lastBtn: getAll('[data-datagrid-last]'),
      pageSizeSelect: getAll('[data-datagrid-pagesize]')
    };

    // Restore search term if present
    if (this.searchTerm && this.searchInput) {
      this.searchInput.value = this.searchTerm;
      
      // If autoFocusSearch is enabled, focus and move caret to end
      if (this.options.autoFocusSearch) {
        setTimeout(() => {
           this.searchInput.focus();
           this.searchInput.setSelectionRange(this.searchInput.value.length, this.searchInput.value.length);
        }, 0);
      }
    }
    
    // Generate skeleton rows based on current pageSize
    this.updateSkeletonRows();
  }

  /**
   * Update skeleton loading state to show correct number of rows based on pageSize
   * This ensures the skeleton matches the actual grid appearance
   */
  private updateSkeletonRows(): void {
    const skeletonHeader = this.loadingEl?.querySelector('[data-datagrid-skeleton-header]');
    const skeletonBody = this.loadingEl?.querySelector('[data-datagrid-skeleton-body]');
    
    if (!skeletonHeader || !skeletonBody) return;
    
    const columnCount = this.options.columns.length;
    const widths = ['w-16', 'w-20', 'w-24', 'w-28', 'w-32', 'w-36', 'w-40', 'w-44'];
    
    // Generate header skeleton
    let headerHtml = '<tr>';
    for (let i = 0; i < columnCount; i++) {
      const width = widths[i % widths.length];
      headerHtml += `<th><div class="skeleton h-4 ${width}"></div></th>`;
    }
    headerHtml += '</tr>';
    skeletonHeader.innerHTML = headerHtml;
    
    // Generate body skeleton rows based on pageSize
    let bodyHtml = '';
    for (let row = 0; row < this.pageSize; row++) {
      bodyHtml += '<tr>';
      for (let col = 0; col < columnCount; col++) {
        // Vary widths slightly for visual interest
        const widthIndex = (row + col) % widths.length;
        const width = widths[widthIndex];
        bodyHtml += `<td><div class="skeleton h-4 ${width}"></div></td>`;
      }
      bodyHtml += '</tr>';
    }
    skeletonBody.innerHTML = bodyHtml;
  }

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    // Handle input changes (update state, but don't trigger search for server mode)
    this.searchInput.addEventListener('input', () => {
      this.searchTerm = this.searchInput.value;
      
      // Client mode: immediate filtering on input
      if (this.mode === 'client' && !this.options.onSearch) {
        this.applyFilters();
        this.currentPage = 1;
        this.render();
      }
    });

    // Handle Enter key for server mode search
    this.searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.triggerSearch();
      }
    });

    // Pagination - bind to all instances
    this.paginationElements.firstBtn.forEach(btn => btn.addEventListener('click', () => this.goToFirstPage()));
    this.paginationElements.prevBtn.forEach(btn => btn.addEventListener('click', () => this.goToPreviousPage()));
    this.paginationElements.nextBtn.forEach(btn => btn.addEventListener('click', () => this.goToNextPage()));
    this.paginationElements.lastBtn.forEach(btn => btn.addEventListener('click', () => this.goToLastPage()));
    
    this.paginationElements.pageSizeSelect.forEach(select => {
      select.addEventListener('change', (e) => {
        // Update all other selects to match
        const newValue = (e.target as HTMLSelectElement).value;
        this.paginationElements.pageSizeSelect.forEach(s => s.value = newValue);
        
        this.pageSize = parseInt(newValue);
        this.currentPage = 1;
        this.handlePageChange();
      });
    });

    // Refresh
    const refreshBtn = this.container.querySelector('[data-datagrid-refresh]');
    refreshBtn?.addEventListener('click', () => this.refresh());

    // Columns
    const columnsBtn = this.container.querySelector('[data-datagrid-columns]');
    columnsBtn?.addEventListener('click', () => this.openColumnChooser());

    // Properties
    const propsBtn = this.container.querySelector('[data-datagrid-properties]');
    propsBtn?.addEventListener('click', () => this.openGridProperties());

    // Export
    const exportBtn = this.container.querySelector('[data-datagrid-export]');
    exportBtn?.addEventListener('click', () => this.exportData());

    // Event delegation for row clicks on tbody (more reliable than per-row handlers)
    this.tbody.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT') return; // Ignore checkbox clicks
      
      const tr = target.closest('tr');
      if (!tr) return;
      
      const rowIndex = parseInt(tr.dataset.rowIndex || '-1');
      if (rowIndex >= 0 && this.options.onRowClick) {
        const pageData = this.getPageData();
        const row = pageData[rowIndex];
        if (row) {
          this.options.onRowClick(row);
        }
      }
    });

    this.tbody.addEventListener('dblclick', (e) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT') return; // Ignore checkbox clicks
      
      const tr = target.closest('tr');
      if (!tr) return;
      
      const rowIndex = parseInt(tr.dataset.rowIndex || '-1');
      if (rowIndex >= 0 && this.options.onRowDoubleClick) {
        const pageData = this.getPageData();
        const row = pageData[rowIndex];
        if (row) {
          this.options.onRowDoubleClick(row);
        }
      }
    });
  }

  /**
   * Trigger the search action
   */
  private triggerSearch(): void {
    // If external search handler provided, delegate to it
    if (this.options.onSearch) {
      this.options.onSearch(this.searchTerm);
      return;
    }
    
    if (this.mode === 'server') {
      this.currentPage = 1;
      this.loadPageFromAPI();
    }
  }

  /**
   * Load ALL data from REST API for client mode
   * Fetches entire dataset once, then filters/sorts locally
   */
  private async loadAllDataFromAPI(): Promise<void> {
    this.showLoading();
    
    try {
      if (!this.options.apiEndpoint) return;
      
      // Get custom headers if provided (can be object or function)
      const customHeaders = typeof this.options.fetchHeaders === 'function'
        ? this.options.fetchHeaders()
        : this.options.fetchHeaders;
      
      const response = await fetch(this.options.apiEndpoint, {
        headers: customHeaders
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      // Handle both array response and object with data property
      this.data = Array.isArray(result) ? result : result.data || [];
      this.filteredData = [...this.data];
      
      // Notify callback of loaded data
      if (this.options.onDataLoaded) {
        this.options.onDataLoaded(this.data, this.data.length);
      }
    } catch (error) {
      console.error('Error loading data from API:', error);
      this.data = [];
      this.filteredData = [];
    } finally {
      this.hideLoading();
    }
  }

  /**
   * Load single PAGE from REST API for server mode
   * Sends pagination, search, and sort parameters to server
   */
  private async loadPageFromAPI(): Promise<void> {
    this.showLoading();
    
    try {
      if (!this.options.apiEndpoint) return;
      
      // Build query parameters for server-side processing
      const params = new URLSearchParams({
        page: this.currentPage.toString(),
        pageSize: this.pageSize.toString(),
      });
      
      if (this.searchTerm) {
        params.append('search', this.searchTerm);
      }
      
      if (this.sortColumn && this.sortDirection !== 'none') {
        params.append('sortBy', this.sortColumn);
        params.append('sortDir', this.sortDirection);
      }
      
      // Properly merge query params if endpoint already has some
      const baseUrl = this.options.apiEndpoint;
      let url: string;
      if (baseUrl.includes('?')) {
        // Endpoint already has query params - append with &
        url = `${baseUrl}&${params.toString()}`;
      } else {
        url = `${baseUrl}?${params.toString()}`;
      }
      
      // Get custom headers if provided (can be object or function)
      const customHeaders = typeof this.options.fetchHeaders === 'function'
        ? this.options.fetchHeaders()
        : this.options.fetchHeaders;
      
      const response = await fetch(url, {
        headers: customHeaders
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      // Expected server response format:
      // { data: [...], total: 1000, page: 1, pageSize: 10 }
      this.data = result.data || [];
      this.serverTotalRecords = result.total || 0;
      
      // In server mode, filteredData is same as data (server did the filtering)
      this.filteredData = [...this.data];
      
      // Notify callback of loaded data
      if (this.options.onDataLoaded) {
        this.options.onDataLoaded(this.data, this.serverTotalRecords);
      }
      
      this.render();
    } catch (error) {
      console.error('Error loading page from API:', error);
      this.data = [];
      this.filteredData = [];
      this.render();
    } finally {
      this.hideLoading();
    }
  }

  private startResize(e: MouseEvent, column: DataGridColumn, th: HTMLTableCellElement): void {
    e.preventDefault();
    e.stopPropagation();
    
    this.resizingColumn = column;
    this.resizeStartX = e.pageX;
    this.resizeStartWidth = th.offsetWidth;
    
    this.resizeHandler = (e) => this.doResize(e, th);
    this.stopResizeHandler = () => this.stopResize();
    
    document.addEventListener('mousemove', this.resizeHandler);
    document.addEventListener('mouseup', this.stopResizeHandler);
    
    document.body.style.cursor = 'col-resize';
  }

  private doResize(e: MouseEvent, th: HTMLTableCellElement): void {
    if (!this.resizingColumn) return;
    
    const diff = e.pageX - this.resizeStartX;
    const newWidth = Math.max(50, this.resizeStartWidth + diff); // Min width 50px
    
    const widthStr = `${newWidth}px`;
    this.resizingColumn.width = widthStr;
    th.style.width = widthStr;
  }

  private stopResize(): void {
    if (this.resizeHandler) document.removeEventListener('mousemove', this.resizeHandler);
    if (this.stopResizeHandler) document.removeEventListener('mouseup', this.stopResizeHandler);
    
    document.body.style.cursor = '';
    
    if (this.options.onColumnChange) {
        this.options.onColumnChange(this.options.columns);
    }
    
    this.resizingColumn = null;
    this.resizeHandler = null;
    this.stopResizeHandler = null;
  }

  /**
   * Render table headers
   */
  private renderHeaders(): void {
    this.headerRow.innerHTML = '';

    // Add checkbox column if selectable
    if (this.options.selectable !== false) {
      const checkTh = document.createElement('th');
      checkTh.style.width = '50px';
      checkTh.innerHTML = '<input type="checkbox" class="checkbox checkbox-sm" data-select-all />';
      const selectAllCheckbox = checkTh.querySelector('input') as HTMLInputElement;
      selectAllCheckbox.addEventListener('change', () => this.toggleSelectAll(selectAllCheckbox.checked));
      this.headerRow.appendChild(checkTh);
    }

    this.options.columns.forEach((column) => {
      if (column.visible === false) return;

      const th = document.createElement('th');
      th.style.position = 'relative';
      
      if (column.align) {
        th.style.textAlign = column.align;
      }
      
      if (column.width) {
        th.style.width = column.width;
      }

      // Content container
      const content = document.createElement('div');
      content.className = 'flex items-center gap-1 w-full h-full overflow-hidden';
      
      const text = document.createElement('span');
      text.className = 'truncate flex-1';
      text.textContent = column.header;
      content.appendChild(text);

      if (column.sortable !== false && this.options.sortable !== false) {
        th.classList.add('cursor-pointer', 'hover:bg-base-200');
        th.setAttribute('data-sort-field', column.field);
        
        const icon = document.createElement('i');
        icon.className = 'fas fa-sort text-base-content/30 sort-icon';
        
        if (this.sortColumn === column.field && this.sortDirection !== 'none') {
            icon.className = `fas fa-sort-${this.sortDirection === 'asc' ? 'up' : 'down'} text-primary sort-icon`;
        }
        content.appendChild(icon);
        
        th.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            if (target.classList.contains('resize-handle')) return;
            if (target.closest('.column-filter-dropdown')) return; // Ignore filter dropdown clicks
            this.sortBy(column.field);
        });
      }

      // Column filter button (if filterable)
      if (column.filterable && this.options.onColumnFilter) {
        const filterContainer = document.createElement('div');
        filterContainer.className = 'column-filter-dropdown';
        
        const filterBtn = document.createElement('button');
        filterBtn.type = 'button';
        const existingFilter = this.columnFilters.get(column.field);
        const hasFilter = existingFilter !== undefined && existingFilter !== '' && 
          (typeof existingFilter === 'string' ? existingFilter !== '' : existingFilter.value !== '');
        filterBtn.className = `btn btn-ghost btn-xs px-1 min-h-0 h-5 ${hasFilter ? 'text-primary border border-primary' : 'text-base-content/40 hover:text-base-content'}`;
        filterBtn.innerHTML = '<i class="fas fa-ellipsis-v text-xs"></i>';
        filterBtn.setAttribute('data-filter-btn', column.field);
        filterContainer.appendChild(filterBtn);
        
        // Create dropdown content (will be appended to body for proper positioning)
        const dropdownContent = document.createElement('div');
        const isDateFilter = column.filterType === 'date';
        dropdownContent.className = `fixed p-3 shadow-lg bg-base-100 rounded-box border border-base-300 z-[9999] ${isDateFilter ? 'w-72' : 'w-56'} hidden`;
        dropdownContent.setAttribute('data-filter-dropdown', column.field);
        
        // Track DatePicker instance for date filters
        let datePickerInstance: DatePicker | null = null;
        
        if (isDateFilter) {
          // Date filter with operator and DatePicker
          const existingDateFilter = existingFilter as ColumnFilterValue | undefined;
          const currentOperator = existingDateFilter?.operator || 'gt';
          
          dropdownContent.innerHTML = `
            <div class="flex flex-col gap-2">
              <label class="text-xs font-semibold text-base-content/70">Filter: ${column.header}</label>
              <select class="select select-sm select-bordered w-full" data-filter-operator="${column.field}">
                <option value="eq" ${currentOperator === 'eq' ? 'selected' : ''}>On (equals)</option>
                <option value="lt" ${currentOperator === 'lt' ? 'selected' : ''}>Before</option>
                <option value="gt" ${currentOperator === 'gt' ? 'selected' : ''}>After</option>
              </select>
              <div id="datepicker-container-${column.field}" class="w-full"></div>
              <div class="flex gap-2 justify-end mt-1">
                <button class="btn btn-xs btn-ghost" data-filter-clear="${column.field}">Clear</button>
                <button class="btn btn-xs btn-primary" data-filter-apply="${column.field}">Apply</button>
              </div>
            </div>
          `;
        } else {
          // Text filter (default)
          const currentValue = typeof existingFilter === 'string' ? existingFilter : (existingFilter?.value || '');
          dropdownContent.innerHTML = `
            <div class="flex flex-col gap-2">
              <label class="text-xs font-semibold text-base-content/70">Filter: ${column.header}</label>
              <input type="text" 
                     class="input input-sm input-bordered w-full" 
                     placeholder="Enter filter value..."
                     data-filter-field="${column.field}"
                     value="${currentValue}" />
              <div class="flex gap-2 justify-end">
                <button class="btn btn-xs btn-ghost" data-filter-clear="${column.field}">Clear</button>
                <button class="btn btn-xs btn-primary" data-filter-apply="${column.field}">Apply</button>
              </div>
            </div>
          `;
        }
        
        // Append to body so it's not clipped by table overflow
        document.body.appendChild(dropdownContent);
        
        // Toggle dropdown visibility on button click
        filterBtn.addEventListener('click', async (e) => {
          e.stopPropagation();
          
          // Close all other filter dropdowns first
          document.querySelectorAll('[data-filter-dropdown]').forEach(dd => {
            if (dd !== dropdownContent) dd.classList.add('hidden');
          });
          
          // Position dropdown below the button
          const btnRect = filterBtn.getBoundingClientRect();
          dropdownContent.style.top = `${btnRect.bottom + 4}px`;
          const dropdownWidth = isDateFilter ? 288 : 224; // w-72 or w-56
          dropdownContent.style.left = `${Math.max(8, btnRect.right - dropdownWidth)}px`;
          
          dropdownContent.classList.toggle('hidden');
          
          // Focus the input when opening
          if (!dropdownContent.classList.contains('hidden')) {
            if (isDateFilter) {
              // Initialize DatePicker if not already done
              if (!datePickerInstance) {
                const datePickerContainer = dropdownContent.querySelector(`#datepicker-container-${column.field}`) as HTMLElement;
                if (datePickerContainer) {
                  const existingDateFilter = this.columnFilters.get(column.field) as ColumnFilterValue | undefined;
                  const initialDate = existingDateFilter?.value ? new Date(existingDateFilter.value) : null;
                  
                  datePickerInstance = new DatePicker(datePickerContainer, {
                    name: `filter-date-${column.field}`,
                    value: initialDate,
                    size: 'sm',
                    format: 'DD MMM YYYY',
                    showTodayButton: true,
                    showClearButton: true,
                    placeholder: 'Select date...'
                  });
                  await datePickerInstance.initialize();
                }
              }
            } else {
              const input = dropdownContent.querySelector('input') as HTMLInputElement;
              input?.focus();
            }
          }
        });
        
        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
          if (!filterContainer.contains(e.target as Node) && !dropdownContent.contains(e.target as Node)) {
            dropdownContent.classList.add('hidden');
          }
        });
        
        dropdownContent.addEventListener('click', (e) => e.stopPropagation());
        
        // Handle filter input (for text filters only)
        if (!isDateFilter) {
          const filterInput = dropdownContent.querySelector(`input[data-filter-field="${column.field}"]`) as HTMLInputElement;
          filterInput?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              this.applyColumnFilter(column.field, filterInput.value);
              dropdownContent.classList.add('hidden');
            }
          });
        }
        
        // Handle apply button
        const applyBtn = dropdownContent.querySelector(`button[data-filter-apply="${column.field}"]`);
        applyBtn?.addEventListener('click', () => {
          if (isDateFilter) {
            const operatorSelect = dropdownContent.querySelector(`select[data-filter-operator="${column.field}"]`) as HTMLSelectElement;
            const dateValue = datePickerInstance?.getValue();
            // Format date as YYYY-MM-DD for API
            const formattedDate = dateValue ? this.formatDateForApi(dateValue) : '';
            this.applyColumnFilter(column.field, { value: formattedDate, operator: operatorSelect?.value as 'eq' | 'lt' | 'gt' });
          } else {
            const input = dropdownContent.querySelector(`input[data-filter-field="${column.field}"]`) as HTMLInputElement;
            this.applyColumnFilter(column.field, input?.value || '');
          }
          dropdownContent.classList.add('hidden');
        });
        
        // Handle clear button
        const clearBtn = dropdownContent.querySelector(`button[data-filter-clear="${column.field}"]`);
        clearBtn?.addEventListener('click', () => {
          if (isDateFilter) {
            const operatorSelect = dropdownContent.querySelector(`select[data-filter-operator="${column.field}"]`) as HTMLSelectElement;
            if (operatorSelect) operatorSelect.value = 'eq';
            datePickerInstance?.setValue(null);
            this.applyColumnFilter(column.field, { value: '', operator: 'eq' });
          } else {
            const input = dropdownContent.querySelector(`input[data-filter-field="${column.field}"]`) as HTMLInputElement;
            if (input) input.value = '';
            this.applyColumnFilter(column.field, '');
          }
          dropdownContent.classList.add('hidden');
        });
        
        // Note: dropdownContent is appended to body, not filterContainer
        content.appendChild(filterContainer);
      }
      
      th.appendChild(content);

      // Resize handle
      const handle = document.createElement('div');
      handle.className = 'resize-handle absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary z-10';
      handle.addEventListener('mousedown', (e) => this.startResize(e, column, th));
      handle.addEventListener('click', (e) => e.stopPropagation());
      th.appendChild(handle);

      this.headerRow.appendChild(th);
    });
  }

  /**
   * Render table body
   */
  private render(): void {
    const paginationInfo = this.getPaginationInfo();
    const pageData = this.getPageData();
    
    logger.debug('render called', { 
      pageDataCount: pageData.length, 
      selectedRowsCount: this.selectedRows.size,
      selectedRowIds: Array.from(this.selectedRows).map(r => this.getRowId(r))
    });

    // Clear tbody
    this.tbody.innerHTML = '';

    // Show/hide empty state
    if (this.filteredData.length === 0) {
      this.emptyEl.classList.remove('hidden');
      this.tbody.closest('table')?.classList.add('hidden');
    } else {
      this.emptyEl.classList.add('hidden');
      this.tbody.closest('table')?.classList.remove('hidden');

      // Render rows
      pageData.forEach((row, rowIndex) => {
        const tr = document.createElement('tr');
        tr.dataset.rowIndex = rowIndex.toString(); // For event delegation
        const isSelected = this.isRowSelected(row);
        
        // Use border for hover instead of background to avoid text visibility issues
        tr.classList.add('hover:outline', 'hover:outline-2', 'hover:outline-primary', 'hover:outline-offset-[-2px]', 'hover:rounded-lg');
        
        if (isSelected) {
          tr.classList.add('bg-primary', 'text-primary-content');
        }

        // Add checkbox column if selectable
        if (this.options.selectable !== false) {
          const checkTd = document.createElement('td');
          checkTd.innerHTML = `<input type="checkbox" class="checkbox checkbox-sm ${isSelected ? 'checkbox-primary [--chkbg:theme(colors.primary.content)] [--chkfg:theme(colors.primary.DEFAULT)]' : ''}" ${isSelected ? 'checked' : ''} />`;
          const checkbox = checkTd.querySelector('input') as HTMLInputElement;
          checkbox.addEventListener('change', (e) => {
            e.stopPropagation();
            this.toggleRowSelection(row, checkbox.checked);
          });
          tr.appendChild(checkTd);
        }

        const cells: HTMLTableCellElement[] = [];
        this.options.columns.forEach((column) => {
          if (column.visible === false) return;

          const td = document.createElement('td');
          td.classList.add('truncate');
          const value = this.getNestedValue(row, column.field);
          
          if (column.align) {
            td.style.textAlign = column.align;
          }

          if (column.formatter) {
            let formattedHtml = column.formatter(value, row);
            // Apply filter highlighting to formatted content if column is filterable with active filter
            if (column.filterable && this.columnFilters.has(column.field) && this.columnFilters.get(column.field)) {
              formattedHtml = this.highlightTextInHtml(formattedHtml, column.field);
            }
            td.innerHTML = formattedHtml;
          } else if (column.filterable && this.columnFilters.has(column.field) && this.columnFilters.get(column.field)) {
            // Apply filter highlighting for filterable columns with active filter
            td.innerHTML = this.highlightFilterMatch(value != null ? String(value) : '', column.field);
          } else {
            td.textContent = value != null ? value : '';
          }

          cells.push(td);
          tr.appendChild(td);
        });
        
        // Apply rounded corners to first and last cells of selected rows
        if (isSelected && cells.length > 0) {
          const firstCell = this.options.selectable !== false ? tr.firstElementChild as HTMLTableCellElement : cells[0];
          const lastCell = cells[cells.length - 1];
          if (firstCell) firstCell.classList.add('rounded-l-lg');
          if (lastCell) lastCell.classList.add('rounded-r-lg');
        }

        // Row click/dblclick styling (handlers are set up via event delegation in setupEventListeners)
        if (this.options.onRowClick || this.options.onRowDoubleClick) {
          tr.style.cursor = 'pointer';
        }

        this.tbody.appendChild(tr);
      });
      
      // Update select all checkbox state
      this.updateSelectAllCheckbox();
    }

    // Update pagination display
    this.updatePaginationDisplay(paginationInfo);
  }

  /**
   * Apply search filters
   */
  private applyFilters(): void {
    if (!this.searchTerm) {
      this.filteredData = [...this.data];
      return;
    }

    const searchLower = this.searchTerm.toLowerCase();
    this.filteredData = this.data.filter((row) => {
      return this.options.columns.some((column) => {
        const value = this.getNestedValue(row, column.field);
        return value != null && value.toString().toLowerCase().includes(searchLower);
      });
    });
  }

  /**
   * Update header sort icon
   */
  private updateHeaderSortIcon(th: HTMLTableCellElement, column: DataGridColumn): void {
    const icon = th.querySelector('.sort-icon');
    if (!icon) return;

    const isSorted = this.sortColumn === column.field && this.sortDirection !== 'none';
    
    if (isSorted) {
      const iconName = this.sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
      icon.className = `fas ${iconName} ml-1 text-primary sort-icon`;
    } else {
      icon.className = 'fas fa-sort ml-1 text-base-content/30 sort-icon';
    }
  }

  /**
   * Update all header sort icons
   */
  private updateAllHeaderSortIcons(): void {
    this.options.columns.forEach((column) => {
      if (column.sortable !== false && this.options.sortable !== false) {
        const th = this.headerRow.querySelector(`[data-sort-field="${column.field}"]`) as HTMLTableCellElement;
        if (th) {
          this.updateHeaderSortIcon(th, column);
        }
      }
    });
  }

  /**
   * Highlight matching filter text in a cell value
   */
  private highlightFilterMatch(text: string, field: string): string {
    const filterValue = this.columnFilters.get(field);
    if (!filterValue || !text) return this.escapeHtml(text || '');
    
    // Extract string value - handle both string and ColumnFilterValue
    let filterString: string;
    if (typeof filterValue === 'string') {
      filterString = filterValue;
    } else {
      // For date filters, don't highlight - just return escaped text
      if (filterValue.operator && filterValue.operator !== 'like') {
        return this.escapeHtml(text || '');
      }
      filterString = filterValue.value;
    }
    
    if (!filterString) return this.escapeHtml(text || '');
    
    const escapedText = this.escapeHtml(text);
    const escapedFilter = this.escapeHtml(filterString);
    
    // Case-insensitive match
    const regex = new RegExp(`(${this.escapeRegex(escapedFilter)})`, 'gi');
    return escapedText.replace(regex, '<mark class="bg-warning/50 text-warning-content rounded px-0.5">$1</mark>');
  }

  /**
   * Escape HTML special characters
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Format a Date object to YYYY-MM-DD string for API
   */
  private formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Escape special regex characters
   */
  private escapeRegex(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Highlight matching text within HTML content (preserves HTML structure)
   */
  private highlightTextInHtml(html: string, field: string): string {
    const filterValue = this.columnFilters.get(field);
    if (!filterValue || !html) return html;
    
    // Extract string value - handle both string and ColumnFilterValue
    let filterString: string;
    if (typeof filterValue === 'string') {
      filterString = filterValue;
    } else {
      // For date filters, don't highlight - just return original html
      if (filterValue.operator && filterValue.operator !== 'like') {
        return html;
      }
      filterString = filterValue.value;
    }
    
    if (!filterString) return html;
    
    // Create a temporary container to parse the HTML
    const container = document.createElement('div');
    container.innerHTML = html;
    
    // Walk through all text nodes and apply highlighting
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
    const textNodes: Text[] = [];
    
    while (walker.nextNode()) {
      textNodes.push(walker.currentNode as Text);
    }
    
    const regex = new RegExp(`(${this.escapeRegex(filterString)})`, 'gi');
    
    textNodes.forEach(node => {
      const text = node.textContent || '';
      if (regex.test(text)) {
        // Reset lastIndex since we used test()
        regex.lastIndex = 0;
        const fragment = document.createDocumentFragment();
        let lastIndex = 0;
        let match;
        
        while ((match = regex.exec(text)) !== null) {
          // Add text before match
          if (match.index > lastIndex) {
            fragment.appendChild(document.createTextNode(text.substring(lastIndex, match.index)));
          }
          // Add highlighted match
          const mark = document.createElement('mark');
          mark.className = 'bg-warning/50 text-warning-content rounded px-0.5';
          mark.textContent = match[1];
          fragment.appendChild(mark);
          lastIndex = match.index + match[0].length;
        }
        
        // Add remaining text
        if (lastIndex < text.length) {
          fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
        }
        
        node.parentNode?.replaceChild(fragment, node);
      }
    });
    
    return container.innerHTML;
  }

  /**
   * Apply column filter
   */
  private applyColumnFilter(field: string, value: string | ColumnFilterValue): void {
    const isEmpty = typeof value === 'string' ? !value : !value.value;
    if (!isEmpty) {
      this.columnFilters.set(field, value);
    } else {
      this.columnFilters.delete(field);
    }
    
    // Update filter button appearance
    this.updateColumnFilterButtons();
    
    // Notify external handler
    if (this.options.onColumnFilter) {
      this.options.onColumnFilter(new Map(this.columnFilters));
    }
  }

  /**
   * Update column filter button appearances
   */
  private updateColumnFilterButtons(): void {
    this.options.columns.forEach((column) => {
      if (!column.filterable) return;
      
      const filterBtn = this.headerRow.querySelector(`[data-filter-btn="${column.field}"]`);
      if (filterBtn) {
        const filterValue = this.columnFilters.get(column.field);
        const hasFilter = filterValue !== undefined && 
          (typeof filterValue === 'string' ? filterValue !== '' : filterValue.value !== '');
        filterBtn.className = `btn btn-ghost btn-xs px-1 min-h-0 h-5 ${hasFilter ? 'text-primary border border-primary' : 'text-base-content/40 hover:text-base-content'}`;
      }
    });
  }

  /**
   * Get current column filters (for external use)
   */
  public getColumnFilters(): Map<string, string | ColumnFilterValue> {
    return new Map(this.columnFilters);
  }

  /**
   * Clear all column filters
   */
  public clearColumnFilters(): void {
    this.columnFilters.clear();
    this.updateColumnFilterButtons();
    
    // Update input values in dropdowns
    this.columnFilters.forEach((_, field) => {
      const input = this.headerRow.querySelector(`input[data-filter-field="${field}"]`) as HTMLInputElement;
      if (input) input.value = '';
    });
    
    if (this.options.onColumnFilter) {
      this.options.onColumnFilter(new Map(this.columnFilters));
    }
  }

  /**
   * Sort by column
   */
  private sortBy(field: string): void {
    if (this.sortColumn === field) {
      // Cycle through: asc → desc → none → asc
      if (this.sortDirection === 'asc') {
        this.sortDirection = 'desc';
      } else if (this.sortDirection === 'desc') {
        this.sortDirection = 'none';
      } else {
        this.sortDirection = 'asc';
      }
    } else {
      this.sortColumn = field;
      this.sortDirection = 'asc';
    }

    this.updateAllHeaderSortIcons();

    // If external sort handler provided, delegate to it
    if (this.options.onSort && this.sortDirection !== 'none') {
      this.options.onSort(field, this.sortDirection);
      return;
    }

    if (this.mode === 'server') {
      // Server mode: Request sorted data from API
      this.currentPage = 1;
      this.loadPageFromAPI();
    } else {
      // Client mode: Sort locally (skip if sort is off)
      if (this.sortDirection === 'none') {
        // Reset to original filtered order
        this.applyFilters();
        this.render();
        return;
      }
      
      this.filteredData.sort((a, b) => {
        const aVal = a[field];
        const bVal = b[field];

        if (aVal == null) return 1;
        if (bVal == null) return -1;

        let comparison = 0;
        if (typeof aVal === 'string') {
          comparison = aVal.localeCompare(bVal);
        } else {
          comparison = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
        }

        return this.sortDirection === 'asc' ? comparison : -comparison;
      });

      this.render();
    }
  }

  /**
   * Get pagination information
   */
  /**
   * Get pagination information
   */
  public getPaginationInfo(): PaginationInfo {
    let totalRecords: number;
    
    if (this.mode === 'server') {
      // Server mode: Use total from API response
      totalRecords = this.serverTotalRecords;
    } else {
      // Client mode: Use filtered data length
      totalRecords = this.filteredData.length;
    }
    
    const totalPages = Math.ceil(totalRecords / this.pageSize);

    return {
      currentPage: this.currentPage,
      pageSize: this.pageSize,
      totalRecords,
      totalPages: totalPages || 1
    };
  }

  /**
   * Get current page data
   */
  private getPageData(): any[] {
    if (this.mode === 'server') {
      // Server mode: Data is already the current page
      return this.data;
    } else {
      // Client mode: Slice the filtered data
      const startIndex = (this.currentPage - 1) * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      return this.filteredData.slice(startIndex, endIndex);
    }
  }

  /**
   * Update pagination display
   */
  private updatePaginationDisplay(info: PaginationInfo): void {
    const startRecord = info.totalRecords === 0 ? 0 : (info.currentPage - 1) * info.pageSize + 1;
    const endRecord = Math.min(info.currentPage * info.pageSize, info.totalRecords);

    this.paginationElements.rangeStart.forEach(el => el.textContent = startRecord.toString());
    this.paginationElements.rangeEnd.forEach(el => el.textContent = endRecord.toString());
    this.paginationElements.total.forEach(el => el.textContent = info.totalRecords.toString());
    this.paginationElements.currentPage.forEach(el => el.textContent = info.currentPage.toString());
    this.paginationElements.totalPages.forEach(el => el.textContent = info.totalPages.toString());

    // Update page size selects (in case changed programmatically)
    this.paginationElements.pageSizeSelect.forEach(el => el.value = info.pageSize.toString());

    // Enable/disable buttons
    this.paginationElements.firstBtn.forEach(btn => btn.disabled = info.currentPage === 1);
    this.paginationElements.prevBtn.forEach(btn => btn.disabled = info.currentPage === 1);
    this.paginationElements.nextBtn.forEach(btn => btn.disabled = info.currentPage === info.totalPages || info.totalRecords === 0);
    this.paginationElements.lastBtn.forEach(btn => btn.disabled = info.currentPage === info.totalPages || info.totalRecords === 0);
  }

  private goToFirstPage(): void {
    this.currentPage = 1;
    this.handlePageChange();
  }

  private goToPreviousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.handlePageChange();
    }
  }

  private goToNextPage(): void {
    const info = this.getPaginationInfo();
    if (this.currentPage < info.totalPages) {
      this.currentPage++;
      this.handlePageChange();
    }
  }

  private goToLastPage(): void {
    const info = this.getPaginationInfo();
    this.currentPage = info.totalPages;
    this.handlePageChange();
  }

  private handlePageChange(): void {
    if (this.options.onPageChange) {
      this.options.onPageChange(this.currentPage, this.pageSize);
      return;
    }

    if (this.mode === 'server') {
      this.loadPageFromAPI();
    } else {
      this.render();
    }
  }

  /**
   * Check if a row is selected
    } else {
      this.render();
    }
  }

  /**
   * Check if a row is selected
   */
  private isRowSelected(row: any): boolean {
    const rowId = this.getRowId(row);
    const isSelected = Array.from(this.selectedRows).some(selectedRow => 
      this.getRowId(selectedRow) === rowId
    );
    return isSelected;
  }

  /**
   * Get unique identifier for a row
   */
  private getRowId(row: any): string {
    // If custom idField is specified, use it
    if (this.options.idField) {
      if (typeof this.options.idField === 'function') {
        return this.options.idField(row);
      }
      if (row[this.options.idField] !== undefined) {
        return String(row[this.options.idField]);
      }
    }
    
    // Try to find a unique ID field, otherwise use JSON string
    // First check common ID fields
    const idFields = ['id', 'accountId', 'itemCode', '_id'];
    for (const field of idFields) {
      if (row[field] !== undefined) {
        return String(row[field]);
      }
    }
    // Look for any field ending in 'Id' (e.g., ContactEducationHistoryId, ContactWorkHistoryId)
    for (const field of Object.keys(row)) {
      if (field.endsWith('Id') && row[field] !== undefined && row[field] !== null) {
        return String(row[field]);
      }
    }
    return JSON.stringify(row);
  }

  /**
   * Toggle row selection
   */
  private toggleRowSelection(row: any, selected: boolean): void {
    const rowId = this.getRowId(row);
    logger.debug('toggleRowSelection', { 
      rowId, 
      selected, 
      selectedRowsBefore: this.selectedRows.size,
      selectedRowIds: Array.from(this.selectedRows).map(r => this.getRowId(r))
    });
    
    if (selected) {
      this.selectedRows.add(row);
    } else {
      // Remove by matching ID
      const rowsToDelete: any[] = [];
      this.selectedRows.forEach(selectedRow => {
        if (this.getRowId(selectedRow) === rowId) {
          rowsToDelete.push(selectedRow);
        }
      });
      rowsToDelete.forEach(r => this.selectedRows.delete(r));
    }
    
    logger.debug('toggleRowSelection after', { 
      selectedRowsAfter: this.selectedRows.size,
      selectedRowIds: Array.from(this.selectedRows).map(r => this.getRowId(r))
    });
    
    // Trigger callback if provided
    if (this.options.onRowSelect) {
      this.options.onRowSelect(Array.from(this.selectedRows));
    }
    
    // Re-render to update highlighting
    this.render();
  }

  /**
   * Toggle select all rows on current page
   */
  private toggleSelectAll(selected: boolean): void {
    const pageData = this.getPageData();
    
    logger.debug('toggleSelectAll', { 
      selected, 
      pageDataCount: pageData.length,
      selectedRowsBefore: this.selectedRows.size,
      pageRowIds: pageData.map(row => this.getRowId(row)),
      selectedRowIds: Array.from(this.selectedRows).map(r => this.getRowId(r))
    });
    
    if (selected) {
      pageData.forEach(row => {
        if (!this.isRowSelected(row)) {
          this.selectedRows.add(row);
        }
      });
    } else {
      // Build a set of IDs to remove from current page
      const pageRowIds = new Set(pageData.map(row => this.getRowId(row)));
      
      // Find and collect rows to delete (avoid modifying Set while iterating)
      const rowsToDelete: any[] = [];
      this.selectedRows.forEach(selectedRow => {
        if (pageRowIds.has(this.getRowId(selectedRow))) {
          rowsToDelete.push(selectedRow);
        }
      });
      
      logger.debug('toggleSelectAll deselecting', { 
        rowsToDeleteCount: rowsToDelete.length,
        rowsToDeleteIds: rowsToDelete.map(r => this.getRowId(r))
      });
      
      // Now delete them
      rowsToDelete.forEach(row => this.selectedRows.delete(row));
    }
    
    logger.debug('toggleSelectAll after', { 
      selectedRowsAfter: this.selectedRows.size,
      selectedRowIds: Array.from(this.selectedRows).map(r => this.getRowId(r))
    });
    
    // Trigger callback if provided
    if (this.options.onRowSelect) {
      this.options.onRowSelect(Array.from(this.selectedRows));
    }
    
    this.render();
  }

  /**
   * Update select all checkbox state
   */
  private updateSelectAllCheckbox(): void {
    const selectAllCheckbox = this.container.querySelector('[data-select-all]') as HTMLInputElement;
    if (!selectAllCheckbox) return;
    
    const pageData = this.getPageData();
    const allSelected = pageData.length > 0 && pageData.every(row => this.isRowSelected(row));
    const someSelected = pageData.some(row => this.isRowSelected(row));
    
    selectAllCheckbox.checked = allSelected;
    selectAllCheckbox.indeterminate = !allSelected && someSelected;
  }

  /**
   * Refresh data
   */
  async refresh(): Promise<void> {
    if (this.mode === 'server') {
      // Server mode: Reload current page from API
      await this.loadPageFromAPI();
    } else {
      // Client mode: Reload all data
      if (this.options.apiEndpoint) {
        await this.loadAllDataFromAPI();
      } else if (this.options.localData) {
        this.data = [...this.options.localData];
        this.filteredData = [...this.data];
      }
      this.applyFilters();
      this.currentPage = 1;
      this.render();
    }
  }

  /**
   * Export data to CSV
   */
  public exportData(): void {
    const headers = this.options.columns.map(col => col.header).join(',');
    const rows = this.filteredData.map(row => {
      return this.options.columns.map(col => {
        const value = row[col.field];
        return value != null ? `"${value.toString().replace(/"/g, '""')}"` : '';
      }).join(',');
    });

    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'export.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Show loading state (skeleton)
   */
  public showLoading(): void {
    // Update skeleton rows in case pageSize has changed
    this.updateSkeletonRows();
    this.loadingEl?.classList.remove('hidden');
    this.tableWrapper?.classList.add('hidden');
  }

  /**
   * Hide loading state
   */
  public hideLoading(): void {
    this.loadingEl?.classList.add('hidden');
    this.tableWrapper?.classList.remove('hidden');
  }

  /**
   * Get current column configuration
   */
  public getColumns(): DataGridColumn[] {
    return [...this.options.columns];
  }

  /**
   * Get currently selected rows
   */
  public getSelectedRows(): any[] {
    return Array.from(this.selectedRows);
  }

  /**
   * Get current grid properties
   */
  public getGridProperties(): { horizontalScroll: boolean; pagingPosition: string; pageSize: number } {
    const isHorizontalScroll = this.container.querySelector('[data-datagrid-table-wrapper]')?.classList.contains('overflow-x-auto') || false;
    
    const topPagination = this.container.querySelector('[data-datagrid-pagination-top]');
    const bottomPagination = this.container.querySelector('[data-datagrid-pagination-bottom]');
    
    let pagingPosition = 'bottom';
    if (topPagination && !topPagination.classList.contains('hidden')) {
      if (bottomPagination && !bottomPagination.classList.contains('hidden')) {
        pagingPosition = 'both';
      } else {
        pagingPosition = 'top';
      }
    }
    
    return {
      horizontalScroll: isHorizontalScroll,
      pagingPosition,
      pageSize: this.pageSize
    };
  }

  /**
   * Set grid properties
   */
  public setGridProperties(props: { horizontalScroll?: boolean; pagingPosition?: string; pageSize?: number }): void {
    // Horizontal Scroll
    if (props.horizontalScroll !== undefined) {
      const wrapper = this.container.querySelector('[data-datagrid-table-wrapper]');
      if (wrapper) {
        if (props.horizontalScroll) {
          wrapper.classList.add('overflow-x-auto');
          wrapper.classList.remove('overflow-hidden');
        } else {
          wrapper.classList.remove('overflow-x-auto');
          wrapper.classList.add('overflow-hidden');
        }
      }
    }

    // Paging Position
    if (props.pagingPosition) {
      const topPagination = this.container.querySelector('[data-datagrid-pagination-top]');
      const bottomPagination = this.container.querySelector('[data-datagrid-pagination-bottom]');
      
      if (topPagination && bottomPagination) {
        topPagination.classList.add('hidden');
        bottomPagination.classList.add('hidden');
        
        if (props.pagingPosition === 'top' || props.pagingPosition === 'both') {
          topPagination.classList.remove('hidden');
        }
        if (props.pagingPosition === 'bottom' || props.pagingPosition === 'both') {
          bottomPagination.classList.remove('hidden');
        }
      }
    }

    // Page Size
    if (props.pageSize !== undefined && props.pageSize !== this.pageSize) {
      this.pageSize = props.pageSize;
      this.currentPage = 1;
      this.handlePageChange();
    }
  }

  /**
   * Open column chooser modal
   */
  public openColumnChooser(): void {
    // Create list items for each column
    const listItems = this.options.columns.map((col, index) => {
      const isVisible = col.visible !== false;
      return `
        <div class="flex items-center gap-3 p-2 bg-base-100 rounded-lg border border-base-300 cursor-move hover:bg-base-200 transition-colors" 
             draggable="true" 
             data-index="${index}"
             data-field="${col.field}">
          <i class="fas fa-grip-vertical text-base-content/30"></i>
          <input type="checkbox" class="checkbox checkbox-sm checkbox-primary" ${isVisible ? 'checked' : ''} />
          <input type="text" class="input input-sm input-ghost w-full font-medium focus:bg-base-100 focus:border-primary" value="${col.header}" placeholder="Column Header" />
        </div>
      `;
    }).join('');

    const html = `
      <div class="flex justify-between items-center mb-4">
        <div class="text-sm text-base-content/70">
          Drag items to reorder. Uncheck to hide columns.
        </div>
        <div class="join">
          <button id="btn-col-select-all" class="btn btn-xs join-item">All</button>
          <button id="btn-col-select-none" class="btn btn-xs join-item">None</button>
        </div>
      </div>
      <div id="column-chooser-list" class="space-y-2 max-h-[60vh] overflow-y-auto entity-form-scrollable p-1">
        ${listItems}
      </div>
    `;

    ModalSystem.show({
      title: 'Choose Columns',
      html,
      width: 'sm',
      buttons: [
        {
          caption: 'Apply',
          primary: true,
          className: 'btn-primary',
          callback: () => {
            this.applyColumnChanges();
            return true;
          }
        },
        {
          caption: 'Cancel'
        }
      ],
      onOpened: () => {
        this.setupColumnChooserDnD();
        this.setupColumnChooserActions();
      }
    });
  }

  private setupColumnChooserActions(): void {
    const list = document.getElementById('column-chooser-list');
    const btnAll = document.getElementById('btn-col-select-all');
    const btnNone = document.getElementById('btn-col-select-none');

    if (!list) return;

    btnAll?.addEventListener('click', () => {
      list.querySelectorAll('input[type="checkbox"]').forEach(cb => {
        (cb as HTMLInputElement).checked = true;
      });
    });

    btnNone?.addEventListener('click', () => {
      list.querySelectorAll('input[type="checkbox"]').forEach(cb => {
        (cb as HTMLInputElement).checked = false;
      });
    });

    // Prevent drag when interacting with inputs
    list.querySelectorAll('input[type="text"]').forEach(input => {
      input.addEventListener('mousedown', (e) => e.stopPropagation());
      input.addEventListener('click', (e) => e.stopPropagation());
      input.addEventListener('dragstart', (e) => {
        e.preventDefault();
        e.stopPropagation();
      });
    });
  }

  private setupColumnChooserDnD(): void {
    const list = document.getElementById('column-chooser-list');
    if (!list) return;

    let draggedItem: HTMLElement | null = null;

    list.querySelectorAll('[draggable="true"]').forEach(item => {
      const el = item as HTMLElement;

      el.addEventListener('dragstart', (e) => {
        draggedItem = el;
        el.classList.add('opacity-50');
        if (e.dataTransfer) {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', el.dataset.index || '');
        }
      });

      el.addEventListener('dragend', () => {
        el.classList.remove('opacity-50');
        draggedItem = null;
        
        // Remove all drag-over classes
        list.querySelectorAll('.border-primary').forEach(i => {
          i.classList.remove('border-primary', 'border-2');
        });
      });

      el.addEventListener('dragover', (e) => {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      });
      
      el.addEventListener('dragenter', (e) => {
        e.preventDefault();
        if (draggedItem && draggedItem !== el) {
          el.classList.add('border-primary', 'border-2');
        }
      });

      el.addEventListener('dragleave', () => {
        el.classList.remove('border-primary', 'border-2');
      });

      el.addEventListener('drop', (e) => {
        e.preventDefault();
        el.classList.remove('border-primary', 'border-2');
        
        if (draggedItem && draggedItem !== el) {
          // Reorder DOM
          const allItems = Array.from(list.children);
          const fromIndex = allItems.indexOf(draggedItem);
          const toIndex = allItems.indexOf(el);
          
          if (fromIndex < toIndex) {
            el.after(draggedItem);
          } else {
            el.before(draggedItem);
          }
        }
      });
    });
  }

  private applyColumnChanges(): void {
    const list = document.getElementById('column-chooser-list');
    if (!list) return;

    const newColumns: DataGridColumn[] = [];
    
    list.querySelectorAll('[data-field]').forEach(item => {
      const field = (item as HTMLElement).dataset.field;
      const checkbox = item.querySelector('input[type="checkbox"]') as HTMLInputElement;
      const headerInput = item.querySelector('input[type="text"]') as HTMLInputElement;
      
      const isVisible = checkbox.checked;
      const newHeader = headerInput ? headerInput.value.trim() : '';
      
      // Find original column config
      const originalCol = this.options.columns.find(c => c.field === field);
      if (originalCol) {
        newColumns.push({
          ...originalCol,
          visible: isVisible,
          header: newHeader || originalCol.header // Fallback to original if empty
        });
      }
    });

    // Update columns and re-render
    this.options.columns = newColumns;
    this.renderHeaders();
    this.render();
    
    // Notify listeners of change (for persistence)
    if (this.options.onColumnChange) {
      this.options.onColumnChange(this.options.columns);
    }
    
    ToasterService.success('Column configuration updated');
  }

  /**
   * Open grid properties modal
   */
  public openGridProperties(): void {
    const isHorizontalScroll = this.container.querySelector('[data-datagrid-table-wrapper]')?.classList.contains('overflow-x-auto');
    
    const topPagination = this.container.querySelector('[data-datagrid-pagination-top]');
    const bottomPagination = this.container.querySelector('[data-datagrid-pagination-bottom]');
    
    let pagingPosition = 'bottom';
    if (topPagination && !topPagination.classList.contains('hidden')) {
      if (bottomPagination && !bottomPagination.classList.contains('hidden')) {
        pagingPosition = 'both';
      } else {
        pagingPosition = 'top';
      }
    }

    // Check if there are any active column filters
    const hasActiveFilters = this.columnFilters.size > 0;

    const html = `
      <div class="flex flex-col gap-3 text-sm w-full">
        <!-- Clear Filters -->
        <div class="form-control w-full">
          <label class="label cursor-pointer grid grid-cols-[1fr_auto] items-center py-2 px-3 border border-base-300 rounded-lg hover:bg-base-200/50 transition-colors ${!hasActiveFilters ? 'opacity-50' : ''}">
            <div class="flex flex-col">
              <span class="text-sm font-medium">Clear All Filters${hasActiveFilters ? ` (${this.columnFilters.size})` : ''}</span>
              <span class="text-xs text-base-content/60 mt-0.5">${hasActiveFilters ? 'Toggle to clear filters and refresh data' : 'No active filters'}</span>
            </div>
            <div class="flex justify-end w-24">
              <input type="checkbox" class="toggle toggle-primary toggle-sm" id="prop-clear-filters" ${!hasActiveFilters ? 'disabled' : ''} />
            </div>
          </label>
        </div>

        <!-- Horizontal Scrolling -->
        <div class="form-control w-full">
          <label class="label cursor-pointer grid grid-cols-[1fr_auto] items-center py-2 px-3 border border-base-300 rounded-lg hover:bg-base-200/50 transition-colors">
            <div class="flex flex-col">
              <span class="text-sm font-medium">Horizontal Scrolling</span>
              <span class="text-xs text-base-content/60 mt-0.5">Enable for wide tables</span>
            </div>
            <div class="flex justify-end w-24">
              <input type="checkbox" class="toggle toggle-primary toggle-sm" id="prop-horizontal-scroll" ${isHorizontalScroll ? 'checked' : ''} />
            </div>
          </label>
        </div>

        <!-- Rows per Page -->
        <div class="form-control w-full">
          <label class="label cursor-pointer grid grid-cols-[1fr_auto] items-center py-2 px-3 border border-base-300 rounded-lg hover:bg-base-200/50 transition-colors">
            <div class="flex flex-col">
              <span class="text-sm font-medium">Rows per Page</span>
              <span class="text-xs text-base-content/60 mt-0.5">Number of rows to display</span>
            </div>
            <div class="flex justify-end w-24">
              <select class="select select-bordered select-xs w-16" id="prop-page-size">
                <option value="5" ${this.pageSize === 5 ? 'selected' : ''}>5</option>
                <option value="10" ${this.pageSize === 10 ? 'selected' : ''}>10</option>
                <option value="15" ${this.pageSize === 15 ? 'selected' : ''}>15</option>
                <option value="25" ${this.pageSize === 25 ? 'selected' : ''}>25</option>
                <option value="50" ${this.pageSize === 50 ? 'selected' : ''}>50</option>
                <option value="75" ${this.pageSize === 75 ? 'selected' : ''}>75</option>
                <option value="100" ${this.pageSize === 100 ? 'selected' : ''}>100</option>
                <option value="250" ${this.pageSize === 250 ? 'selected' : ''}>250</option>
              </select>
            </div>
          </label>
        </div>

        <!-- Paging Controls Position -->
        <div class="form-control w-full">
          <label class="label cursor-pointer grid grid-cols-[1fr_auto] items-center py-2 px-3 border border-base-300 rounded-lg hover:bg-base-200/50 transition-colors">
            <div class="flex flex-col">
              <span class="text-sm font-medium">Paging Controls Position</span>
              <span class="text-xs text-base-content/60 mt-0.5">Where to show pagination</span>
            </div>
            <div class="flex justify-end w-24">
              <select class="select select-bordered select-xs w-full" id="prop-paging-pos">
                <option value="top" ${pagingPosition === 'top' ? 'selected' : ''}>Top</option>
                <option value="bottom" ${pagingPosition === 'bottom' ? 'selected' : ''}>Bottom</option>
                <option value="both" ${pagingPosition === 'both' ? 'selected' : ''}>Both</option>
              </select>
            </div>
          </label>
        </div>
      </div>
    `;

    ModalSystem.show({
      title: 'Grid Properties',
      html,
      width: 'md',
      buttons: [
        {
          caption: 'Apply',
          primary: true,
          className: 'btn-primary',
          callback: () => {
            this.applyGridProperties();
            return true;
          }
        },
        {
          caption: 'Cancel'
        }
      ]
    });
  }

  /**
   * Clear all column filters and refresh the grid
   */
  private clearAllFiltersAndRefresh(): void {
    // Clear all filters
    this.columnFilters.clear();
    
    // Update filter button appearances
    this.updateColumnFilterButtons();
    
    // Clear input values in all filter dropdowns
    document.querySelectorAll('[data-filter-field]').forEach((input) => {
      (input as HTMLInputElement).value = '';
    });
    
    // Reset to first page
    this.currentPage = 1;
    
    // Notify external handler to refresh data
    if (this.options.onColumnFilter) {
      this.options.onColumnFilter(new Map(this.columnFilters));
    }
    
    ToasterService.success('All filters cleared');
  }

  private applyGridProperties(): void {
    // Clear Filters (if checkbox is checked)
    const clearFiltersCheckbox = document.getElementById('prop-clear-filters') as HTMLInputElement;
    if (clearFiltersCheckbox?.checked) {
      this.clearAllFiltersAndRefresh();
    }

    // Horizontal Scroll
    const scrollToggle = document.getElementById('prop-horizontal-scroll') as HTMLInputElement;
    const wrapper = this.container.querySelector('[data-datagrid-table-wrapper]');
    if (wrapper && scrollToggle) {
      if (scrollToggle.checked) {
        wrapper.classList.add('overflow-x-auto');
        wrapper.classList.remove('overflow-hidden');
      } else {
        wrapper.classList.remove('overflow-x-auto');
        wrapper.classList.add('overflow-hidden');
      }
    }

    // Paging Position
    const pagingPosSelect = document.getElementById('prop-paging-pos') as HTMLSelectElement;
    if (pagingPosSelect) {
      const topPagination = this.container.querySelector('[data-datagrid-pagination-top]');
      const bottomPagination = this.container.querySelector('[data-datagrid-pagination-bottom]');
      
      if (topPagination && bottomPagination) {
        topPagination.classList.add('hidden');
        bottomPagination.classList.add('hidden');
        
        if (pagingPosSelect.value === 'top' || pagingPosSelect.value === 'both') {
          topPagination.classList.remove('hidden');
        }
        if (pagingPosSelect.value === 'bottom' || pagingPosSelect.value === 'both') {
          bottomPagination.classList.remove('hidden');
        }
      }
    }

    // Page Size
    const pageSizeSelect = document.getElementById('prop-page-size') as HTMLSelectElement;
    if (pageSizeSelect) {
      const newSize = parseInt(pageSizeSelect.value);
      if (newSize !== this.pageSize) {
        this.pageSize = newSize;
        this.currentPage = 1;
        this.handlePageChange();
      }
    }
    
    ToasterService.success('Grid properties updated');
  }

  /**
   * IComponent interface methods
   */
  async onFormLoaded(): Promise<void> {
    // No-op for IComponent interface
  }

  async onRecordLoaded(data: Record<string, any>): Promise<void> {
    // No-op for IComponent interface
  }

  getData(): Record<string, any> {
    return {
      selectedRows: Array.from(this.selectedRows),
      currentPage: this.currentPage,
      pageSize: this.pageSize
    };
  }

  /**
   * Get all data rows currently in the grid
   */
  getAllData(): any[] {
    return [...this.data];
  }

  setData(data: Record<string, any> | any[]): void {
    let records: any[] | undefined;
    let total = this.serverTotalRecords;
    let pageNumber: number | undefined;

    if (Array.isArray(data)) {
      records = data;
      total = data.length;
    } else if (Array.isArray(data.records)) {
      records = data.records;
      total = typeof data.total === 'number' ? data.total : data.records.length;
      // Support page number from server response
      pageNumber = typeof data.page === 'number' ? data.page : undefined;
    } else if (Array.isArray(data.data)) {
      records = data.data;
      total = typeof data.total === 'number' ? data.total : data.data.length;
      pageNumber = typeof data.page === 'number' ? data.page : undefined;
    }

    if (!records) {
      return;
    }

    // Clear selected rows when data is replaced
    this.selectedRows.clear();

    this.data = records;
    this.filteredData = [...this.data];
    this.serverTotalRecords = total;
    
    // In server mode with external pagination, preserve current page or use server-provided page
    // Only reset to page 1 in client mode or when not using external pagination handler
    if (this.mode === 'server' && this.options.onPageChange) {
      // External handler manages pagination - use server page if provided
      if (pageNumber !== undefined) {
        this.currentPage = pageNumber;
      }
      // Otherwise preserve current page (already set by handlePageChange)
    } else {
      // Client mode or internal server mode - reset to page 1
      this.currentPage = 1;
    }
    
    this.applyFilters();
    this.render();
  }

  validate(): { valid: boolean; message?: string } {
    return { valid: true };
  }

  destroy(): void {
    // Cleanup event listeners and DOM
    this.selectedRows.clear();
  }
}
